<template>
  <Layout>
    <div class="row justify-content-center">
      <div class="col-md-8 col-lg-6 col-xl-5">
        <div class="card bg-pattern">
          <div class="card-body p-4">

            <div class="text-center w-75 m-auto">
              <h2 class="title-code mt-4">Digite o código de acesso</h2>
              <p class="text-muted mb-4">
                Insira o código de 6 dígitos que enviamos para o seu WhatsApp e e-mail.
                O código expira em 10 minutos.
              </p>
            </div>

            <b-alert v-model="isAuthError" variant="danger" dismissible>
              {{ authError }}
            </b-alert>

            <b-form @submit.prevent="confirm">
              <b-row class="inputs py-2 justify-content-center">
                <b-col
                    class="px-1"
                    v-for="(v, index) in inputs"
                    :key="index"
                    cols="2"
                >
                  <input
                      :id="`input-${index}`"
                      :name="`input-${index}`"
                      v-model="values[index]"
                      @keydown.enter="confirm"
                      @keydown="nextInput"
                      @paste="pasteCode"
                      type="text"
                      autocomplete="off"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      class="form-control border-primary text-center input-code"
                      :disabled="loading"
                  />
                </b-col>
              </b-row>

              <b-form-group class="mt-4">
                <b-button
                    type="submit"
                    variant="primary"
                    class="btn-block"
                    :disabled="values.join('').length < 6 || loading"
                >
                  <b-spinner v-if="loading" small label="Carregando..."></b-spinner>
                  <div v-else>Confirmar Código</div>
                </b-button>
              </b-form-group>

              <div class="text-center mt-3">
                <a
                    href="javascript:void(0);"
                    class="text-muted"
                    @click="getNewCode"
                    v-if="!code_resent"
                >
                  Não recebeu o código? <strong>Reenviar</strong>
                </a>
                <span v-else class="text-muted">
                  Reenviar em {{ counter }}s
                </span>
              </div>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@layouts/auth';
import NewAccessCode from "@services/resources/NewAccessCode";
import { getOS, getBrowser } from "@utils/getUserInfos";
import { getPending2FA } from '@utils/auth2faMemory';

const serviceNewCode = NewAccessCode.build();
const safeSessionStorage = {
  getItem(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.warn(`Erro ao acessar sessionStorage (get ${key}):`, error);
      return null;
    }
  },

  setItem(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.warn(`Erro ao acessar sessionStorage (set ${key}):`, error);
    }
  },

  removeItem(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn(`Erro ao acessar sessionStorage (remove ${key}):`, error);
    }
  }
};

export default {
  name: "AccessCode",
  components: { Layout },
  props: {
    email: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      inputs: 6,
      values: [],
      counter: 60,
      code_resent: false,
      loading: false,
      useLoginRoute: false,
      isAuthError: false,
      authError: '',
      resentInterval: null,
      redirectTimeout: null,
    };
  },
  computed: {
    pendingLoginDataFromStore() {
      return this.$store.getters["pendingLoginData"];
    },
  },
  methods: {
    async getNewCode() {
      if (this.code_resent) return;

      if (process.env.VUE_APP_FINGERPRINT_ENABLED === 'true') {
        await this.$store.dispatch('fetchFingerprint');
      }

      this.clearErrorState();

      this.loading = true;

      try {
        safeSessionStorage.removeItem('allow_access_code');

        await serviceNewCode.create({
          email: this.email,
          system_operational: getOS(),
          browser: getBrowser(),
        });

        this.showNotification('Sucesso', 'Código reenviado com sucesso', 'success', 5000);
        this.initResentCounter();

        this.resetValues();

      } catch (error) {
        let errorMessage = 'Erro ao reenviar código';

        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        this.showNotification('Erro', errorMessage, 'danger', 8000);

        this.code_resent = false;
      } finally {
        this.loading = false;
      }
    },

    clearErrorState() {
      safeSessionStorage.removeItem('allow_access_code');

      this.$store.dispatch('clearAuthError');

      this.resetValues()

      this.isAuthError = false;
      this.authError = '';
    },

    resetValues() {
      this.values = Array(this.inputs).fill('');
    },

    isIncorrectCodeError(errorData) {
      if (!errorData) return false;

      const errorValue = errorData.error || errorData.message || errorData.messages || '';
      const errorText = String(errorValue).toLowerCase();

      const errorPhrases = [
        'código informado está incorreto',
        'codigo informado esta incorreto',
        'código incorreto',
        'codigo incorreto'
      ];

      return errorData.credenciais || errorPhrases.some(phrase => errorText.includes(phrase));
    },

    async confirm() {
      try {
        const accessCode = this.values.join("");

        if (accessCode.length !== 6) {
          this.showNotification('Atenção', 'Preencha todos os 6 dígitos', 'warning');
          return;
        }

        const secureData = getPending2FA();

        if (!secureData) {
          throw new Error('Dados de login não encontrados');
        }

        this.loading = true;
        this.isAuthError = false;
        this.authError = '';

        const payload = {
          ...this.pendingLoginDataFromStore,
          password: secureData.getPassword(),
          two_factor_code: accessCode
        };

        await this.$store.dispatch('loginRequest', payload);

        this.handleLoginSuccess();

      } catch (error) {
        if (error.message === 'Dados de login não encontrados') {
          this.showNotification('Sessão expirada', 'Faça login novamente', 'danger');
          this.$store.dispatch("clearLoginDataForAccessCode");
          safeSessionStorage.removeItem('allow_access_code');
          if (this.redirectTimeout) clearTimeout(this.redirectTimeout);
          this.redirectTimeout = setTimeout(() => this.$router.push('/'), 2000);
          return;
        }

        this.handleLoginError(error);

      } finally {
        this.loading = false;
      }
    },

    clearAllInputs() {
      this.resetValues();

      this.$nextTick(() => {
        const firstInput = document.getElementById('input-0');
        if (firstInput) {
          firstInput.focus();
          firstInput.select();
        }
      });
    },

    handleLoginSuccess() {
      safeSessionStorage.removeItem('allow_access_code');
      this.$store.dispatch("clearLoginDataForAccessCode");
    },

    handleLoginError(error) {
      this.loading = false;
      this.isAuthError = true;

      const errorData = error.response ? error.response.data : {};
      const errorMessage = errorData.message || errorData.error || "Código inválido ou expirado.";
      const isIncorrectCode = this.isIncorrectCodeError(errorData);

      this.authError = errorMessage;

      this.showNotification('Erro de Acesso', errorMessage, 'danger', 8000);

      if (isIncorrectCode) {
        this.clearAllInputs();
      } else if (this.useLoginRoute) {
        this.$store.dispatch("clearLoginDataForAccessCode");
        safeSessionStorage.removeItem('allow_access_code');
        if (this.redirectTimeout) clearTimeout(this.redirectTimeout);
        this.redirectTimeout = setTimeout(() => this.$router.push('/'), 2000);
      }
    },

    initResentCounter() {
      this.code_resent = true;
      this.counter = 60;

      if (this.resentInterval) clearInterval(this.resentInterval);

      this.resentInterval = setInterval(() => {
        this.counter--;
        if (this.counter <= 0) {
          this.stopResentCounter();
        }
      }, 1000);
    },

    stopResentCounter() {
      if (this.resentInterval) {
        clearInterval(this.resentInterval);
        this.resentInterval = null;
      }
      this.code_resent = false;
      this.counter = 60;
    },

    showNotification(title = "", message = "", variant = "success", delay = 5000) {
      this.$bvToast.toast(message, {
        variant,
        title,
        autoHideDelay: delay,
      });
    },

    nextInput(event) {
      setTimeout(() => {
        const code = event.keyCode;
        if ((code >= 48 && code <= 57) || (code >= 96 && code <= 105)) {
          const parts = event.target.id.split("-");
          const nextId = parseInt(parts[1]) + 1;
          const nextInput = document.getElementById(`${parts[0]}-${nextId}`);
          if (nextInput) nextInput.focus();
        }
      }, 20);
    },

    pasteCode(e) {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const digits = pastedText.replace(/\D/g, '').slice(0, 6);

      if (digits.length > 0) {
        const newValues = Array(6).fill('');
        for (let i = 0; i < digits.length; i++) {
          newValues[i] = digits[i];
        }

        for (let i = 0; i < newValues.length; i++) {
          this.$set(this.values, i, newValues[i]);
        }

        this.$nextTick(() => {
          const targetIndex = Math.min(digits.length, 5);
          const targetInput = document.getElementById(`input-${targetIndex}`);
          if (targetInput) {
            targetInput.focus();
          }
        });
      }
    },
  },
  watch: {
    values: {
      deep: true,
      handler(current) {
        current.forEach((el, index) => {
          if (el.length > 1) {
            this.$set(this.values, index, el.charAt(0));
          }
        });
      },
    },
  },
  mounted() {
    const stateData = this.pendingLoginDataFromStore;
    const isAllowed = safeSessionStorage.getItem('allow_access_code') === 'true';

    if (stateData && stateData.username && isAllowed) {
      this.useLoginRoute = true;
      this.showNotification(
          'Código Enviado',
          'Verifique seu e-mail e WhatsApp para obter o código.',
          'success',
          8000
      );
    } else {
      this.$store.dispatch("clearLoginDataForAccessCode");
      safeSessionStorage.removeItem('allow_access_code');
      this.$router.push('/');
    }
  },
  beforeDestroy() {
    this.stopResentCounter();

    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }

    if (this.useLoginRoute) {
      this.$store.dispatch("clearLoginDataForAccessCode");
    }
  },
};
</script>

<style scoped lang="scss">
.title-code {
  margin-bottom: 10px;
  font-size: 22px;
  font-weight: 600;
}

.text {
  font-size: 14px;
  color: var(--gray-200);
  line-height: 1.5;
}

.confirm {
  width: 100%;
  display: block;
  margin-bottom: 15px;
}
.new-code {
  font-size: 14px;
  color: var(--gray-200);
  text-align: center;
}
.new-code:disabled,
.new-code.disabled {
  pointer-events: none !important;
  opacity: 0.7;
}

.inputs {
  max-width: 500px;
  row-gap: 10px;
  flex-wrap: nowrap;
}

input {
  text-align: center;
  width: 55px !important;
  height: 50px !important;
  padding: 5px !important;
}
.loading-item {
  padding-top: 30px;
  width: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>